import * as React from 'react'
import { useSnapshot } from 'valtio'
import { historyState } from '../../../../store/historyState.ts'
import { dayHistoryState } from '../../../../store/dayHistoryState.ts'
import { smoothPathToLatLngs, MAX_ZOOM_OUT } from '../../../../helpers/dayHistoryHelper.ts'

/**
 * Renders the day history path on the map as two polylines:
 * - Traveled portion (solid, colored)
 * - Upcoming portion (lighter)
 *
 * Uses the timed smoothPath for both drawing and splitting so the polyline
 * and marker stay perfectly in sync.
 *
 * Uses imperative Google Maps Polyline objects for performance.
 */
export const DayHistoryPolyline: React.FC = () => {
    const map = (useSnapshot(historyState) as typeof historyState).map
    const pathData = (useSnapshot(dayHistoryState) as typeof dayHistoryState).pathData
    const currentTime = (useSnapshot(dayHistoryState) as typeof dayHistoryState).currentTime

    const traveledLineRef = React.useRef<google.maps.Polyline | null>(null)
    const upcomingLineRef = React.useRef<google.maps.Polyline | null>(null)

    // Full path as LatLngLiterals — use smoothPath if available (marker follows it too)
    const fullPath = React.useMemo(() => {
        if (!pathData) return []
        if (pathData.smoothPath) {
            return smoothPathToLatLngs(pathData.smoothPath)
        }
        return pathData.path.map(wp => ({ lat: wp.lat, lng: wp.lng }))
    }, [pathData])

    // Time array matching fullPath indices (for splitting traveled/upcoming)
    const pathTimes = React.useMemo(() => {
        if (!pathData) return []
        if (pathData.smoothPath) {
            return pathData.smoothPath.map(coord => coord[2])
        }
        return pathData.path.map(wp => wp.time)
    }, [pathData])

    // Create polylines on mount, remove on unmount
    React.useEffect(() => {
        if (!map) return

        traveledLineRef.current = new google.maps.Polyline({
            map,
            path: [],
            strokeColor: '#4A90D9',
            strokeOpacity: 1,
            strokeWeight: 4,
            zIndex: 2,
        })

        upcomingLineRef.current = new google.maps.Polyline({
            map,
            path: [],
            strokeColor: '#4A90D9',
            strokeOpacity: 0.3,
            strokeWeight: 3,
            zIndex: 1,
        })

        return () => {
            traveledLineRef.current?.setMap(null)
            upcomingLineRef.current?.setMap(null)
            traveledLineRef.current = null
            upcomingLineRef.current = null
        }
    }, [map])

    // Set full upcoming path when pathData loads
    React.useEffect(() => {
        if (upcomingLineRef.current && fullPath.length) {
            upcomingLineRef.current.setPath(fullPath)
        }
    }, [fullPath])

    // Update traveled/upcoming split based on currentTime
    React.useEffect(() => {
        if (!pathData || !fullPath.length || !pathTimes.length) return

        if (pathTimes.length < 2) {
            traveledLineRef.current?.setPath(fullPath)
            upcomingLineRef.current?.setPath([])
            return
        }

        // During a gap, freeze the split at the last point before the gap started
        // so the polyline highlight doesn't advance while the marker is frozen
        let effectiveTime = currentTime
        const activeGap = pathData.gaps.find(g => currentTime >= g.startTime && currentTime <= g.endTime)
        if (activeGap) {
            effectiveTime = activeGap.startTime
        }

        // Find the index of the last point at or before effectiveTime
        let splitIndex = 0
        for (let i = 0; i < pathTimes.length; i++) {
            if (pathTimes[i] <= effectiveTime) {
                splitIndex = i
            } else {
                break
            }
        }

        // Split cleanly at the index
        const traveled = fullPath.slice(0, splitIndex + 1)
        const upcoming = fullPath.slice(splitIndex)

        traveledLineRef.current?.setPath(traveled)
        upcomingLineRef.current?.setPath(upcoming)
    }, [currentTime, fullPath, pathData, pathTimes])

    // Fit map to path when data first loads
    React.useEffect(() => {
        if (!map || !fullPath.length) return

        const bounds = new google.maps.LatLngBounds()
        fullPath.forEach(p => bounds.extend(p))

        // Fit to bounds, then enforce minimum zoom (don't zoom out past MAX_ZOOM_OUT)
        map.fitBounds(bounds)

        const listener = map.addListener('idle', () => {
            const zoom = map.getZoom() || 0
            if (zoom < MAX_ZOOM_OUT) {
                map.setZoom(MAX_ZOOM_OUT)
            }
            google.maps.event.removeListener(listener)
        })
    }, [map, fullPath])

    // Follow the marker during playback — pan the map if the marker nears the edge
    const isPlaying = (useSnapshot(dayHistoryState) as typeof dayHistoryState).isPlaying
    const currentPosition = (useSnapshot(dayHistoryState) as typeof dayHistoryState).currentPosition

    React.useEffect(() => {
        if (!map || !isPlaying || !currentPosition) return
        if (currentPosition.lat === 0 && currentPosition.lng === 0) return

        const pos = new google.maps.LatLng(currentPosition.lat, currentPosition.lng)
        const bounds = map.getBounds()
        if (!bounds) return

        // Check if marker is within the inner 60% of the viewport (leave 20% margin on each side)
        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()
        const latRange = ne.lat() - sw.lat()
        const lngRange = ne.lng() - sw.lng()
        const margin = 0.2

        const innerBounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(sw.lat() + latRange * margin, sw.lng() + lngRange * margin),
            new google.maps.LatLng(ne.lat() - latRange * margin, ne.lng() - lngRange * margin),
        )

        if (!innerBounds.contains(pos)) {
            map.panTo(pos)
        }
    }, [map, isPlaying, currentPosition])

    return null
}
